const DEFAULT_WISHLIST_NAME = 'My Wishlist';

//ECSR-603: an empty ARRAY, not {}. Every consumer guards with
//Array.isArray, so the old object literal meant that until the first
//successful fetch the cache was silently unusable — and stayed that way
//forever if the fetch failed.
window.wishlistContents = [];

//ECSR-602: screen-reader feedback. Swym's toast (and our fallback toast) is
//purely visual, so activating a heart gave assistive technology no
//success/error signal.
//
//ECSR-613: two permanent live regions, created up front and never retuned.
//This previously used one region whose role/aria-live was switched per
//message; several screen reader + browser combinations do not reliably
//honour a live region's urgency changing after the element already exists,
//so an error could be announced politely or dropped entirely.
const WISHLIST_LIVE_REGION_STYLE =
  'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;';

const WISHLIST_LIVE_REGIONS = {
  polite: { id: 'wishlist-live-region-polite', role: 'status', live: 'polite' },
  assertive: { id: 'wishlist-live-region-assertive', role: 'alert', live: 'assertive' }
};

const getWishlistLiveRegion = (isError = false) => {
  const config = isError ? WISHLIST_LIVE_REGIONS.assertive : WISHLIST_LIVE_REGIONS.polite;
  let region = document.getElementById(config.id);
  if (!region) {
    region = document.createElement('div');
    region.id = config.id;
    //Set before insertion so the region is already correctly typed the first
    //time assistive technology sees it.
    region.setAttribute('role', config.role);
    region.setAttribute('aria-live', config.live);
    region.style.cssText = WISHLIST_LIVE_REGION_STYLE;
    document.body.appendChild(region);
  }
  return region;
};

//Both regions exist from page load so neither is created in the same tick as
//the announcement that uses it.
const ensureWishlistLiveRegions = () => {
  getWishlistLiveRegion(false);
  getWishlistLiveRegion(true);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ensureWishlistLiveRegions);
} else {
  ensureWishlistLiveRegions();
}

//The text is cleared first (and re-set async) so repeating the same action
//re-announces rather than being deduplicated as unchanged content.
const announceWishlistStatus = (message, isError = false) => {
  const region = getWishlistLiveRegion(isError);
  region.textContent = '';
  setTimeout(() => {
    region.textContent = message;
  }, 50);
};

//ECSR-602: the visual fallback toast (used when Swym's notification UI is
//unavailable) is aria-hidden — the live region above already announces the
//same message, and announcing both would double-speak it.
const FALLBACK_TOAST_COLORS = {
  success: '#4CAF50',
  error: '#f44336',
  info: '#2196F3'
};

const showWishlistFallbackToast = (message, type) => {
  const notification = document.createElement('div');
  notification.className = `swym-custom-notification swym-${type}`;
  notification.innerHTML = message;
  notification.setAttribute('aria-hidden', 'true');
  notification.style.cssText = `position: fixed; top: 20px; right: 20px; background: ${FALLBACK_TOAST_COLORS[type]}; color: white; padding: 15px 20px; border-radius: 4px; z-index: 9999; font-size: 14px;`;
  document.body.appendChild(notification);
  setTimeout(() => notification.remove(), 3000);
};

//ECSR-561 QA fix: single guarded writer for cache additions, used by both the
//addToWishlist success callback and the sw:addedtolist event listener — the
//two can both run for one add, so the variantId dedupe keeps the cache free
//of duplicate entries either way.
const addToWishlistContentsCache = ({ variantId, productId, url }) => {
  if (!Array.isArray(window.wishlistContents)) return;
  const alreadyCached = window.wishlistContents.some(
    (item) => String(item.variantId) === String(variantId)
  );
  if (!alreadyCached) {
    window.wishlistContents.push({ variantId, productId, url });
  }
};

window.addToWishlist = ({variantId, productId, url}) => {
  //Ensure wishlist ID exists before trying to add items
  const performAdd = (listId) => {
    window._swat.addToList(listId, {epi: variantId, empi: productId, du: url}, (response) => {
      //Show success notification
      if (window._swat && window._swat.ui && typeof window._swat.ui.showSuccessNotification === 'function') {
        window._swat.ui.showSuccessNotification({ message: '❤️ Added to wishlist.' });
      } else {
        showWishlistFallbackToast('❤️ Added to wishlist.', 'success');
      }
      announceWishlistStatus('Added to wishlist.');
      //ECSR-561 QA fix: record the add in the local cache, mirroring what the
      //remove path already does. This callback only flipped the clicked
      //heart's DOM state, leaving window.wishlistContents stale until the
      //next full fetch — so the cart drawer's post-render resync (which
      //repaints every heart FROM that cache) painted the just-saved item as
      //unsaved. That is exactly the "wishlist on the PDP, add to cart, heart
      //is blank in the mini cart" QA failure. The sw:addedtolist listener
      //below also pushes when Swym emits it; both paths dedupe on variantId.
      addToWishlistContentsCache({ variantId, productId, url });
      updateWishlistButtonState(variantId, productId, true);
    }, (error) => {
      console.error('Swym wishlist error:', error);
      //Show error notification
      if (window._swat && window._swat.ui && typeof window._swat.ui.showErrorNotification === 'function') {
        window._swat.ui.showErrorNotification({ message: 'Error adding to wishlist. Please try again.' });
      } else {
        showWishlistFallbackToast('Error adding to wishlist. Please try again.', 'error');
      }
      announceWishlistStatus('Error adding to wishlist. Please try again.', true);
    });
  };

  //Check if wishlist ID already exists
  if (window._swat.swymCustomWishlistSelectedListId) {
    performAdd(window._swat.swymCustomWishlistSelectedListId);
  } else {
    //Create/get wishlist first, then add item
    window.ensureSingleWishlist(DEFAULT_WISHLIST_NAME, function(res) {
      if (res?.lid) {
        window._swat.swymCustomWishlistSelectedListId = res.lid;
        performAdd(res.lid);
      } else {
        console.error('Failed to create/get wishlist');
        showWishlistFallbackToast('Unable to access wishlist. Please try again.', 'error');
        announceWishlistStatus('Unable to access wishlist. Please try again.', true);
      }
    });
  }
};

//ECSR-561 review fix: rewriting a heart's onclick used to substitute
//`url: window.location.href` for the product URL that Liquid rendered. Swym
//stores that url as the wishlist item's deep link (`du`), so a heart toggled
//anywhere but the product's own page saved a wishlist entry that linked back
//to the wrong page (collection, cart, etc.). The original quoted url is
//stashed on the button the first time we touch it and reused for every
//subsequent rewrite; only buttons that never carried a quoted url fall back
//to window.location.href.
const wishlistButtonUrlExpression = (button) => {
  if (!button.dataset.wishlistUrl) {
    const onclickAttr = button.getAttribute('onclick');
    const urlMatch = onclickAttr
      ? onclickAttr.match(/url:\s*(['"])(.*?)\1/)
      : null;
    if (urlMatch) button.dataset.wishlistUrl = urlMatch[2];
  }
  return button.dataset.wishlistUrl
    ? `'${button.dataset.wishlistUrl}'`
    : 'window.location.href';
};

//ECSR-602: per-product accessible names. Liquid renders the product title on
//the heart (data-wishlist-product-title); labels are rebuilt from it on every
//state flip so a mini cart full of hearts never collapses to identical
//"Add to wishlist" announcements. Hearts without the attribute keep the
//generic labels.
//ECSR-604: the saved-state label now describes removal, because activating a
//saved heart removes the item in place rather than navigating to the wishlist.
const wishlistButtonLabel = (button, isInWishlist) => {
  const title = button.dataset ? button.dataset.wishlistProductTitle : '';
  if (isInWishlist) {
    return title
      ? `Remove ${title} from wishlist`
      : 'Remove from wishlist';
  }
  return title ? `Add ${title} to wishlist` : 'Add to wishlist';
};

//Single place that flips a heart between the two states. The onclick MUST be
//rewritten alongside the visual state: freshly rendered markup always ships
//onclick="addToWishlist(...)" (Liquid has no server-side knowledge of
//wishlist state), so a filled heart left wired to addToWishlist performs a
//silent duplicate-add instead of a removal.
const applyWishlistButtonState = (button, variantId, productId, isInWishlist) => {
  const urlExpression = wishlistButtonUrlExpression(button);

  if (isInWishlist) {
    button.classList.add('swym-added');
    button.setAttribute('title', 'Remove from wishlist');
    button.setAttribute('aria-label', wishlistButtonLabel(button, true));
    if (variantId && productId) {
      button.setAttribute(
        'onclick',
        `removeFromWishlist({ variantId: ${variantId}, productId: ${productId}, url: ${urlExpression} })`
      );
    }
  } else {
    button.classList.remove('swym-added');
    button.setAttribute('title', 'Add to wishlist');
    button.setAttribute('aria-label', wishlistButtonLabel(button, false));
    if (variantId && productId) {
      button.setAttribute(
        'onclick',
        `addToWishlist({ variantId: ${variantId}, productId: ${productId}, url: ${urlExpression} })`
      );
    }
  }
};

//Hearts already rewritten to removeFromWishlist must stay addressable, so
//both onclick spellings are selected.
const getAllWishlistButtons = () => {
  const wishlistButtons = document.querySelectorAll(
    '[onclick*="addToWishlist"], [onclick*="removeFromWishlist"]'
  );
  const idButtons = document.querySelectorAll('[id*="AddToWishlistButton"]');
  return [...wishlistButtons, ...idButtons];
};

//ECSR-606: anchored id matching. The previous `includes('variantId: 123')`
//substring test also matched a rendered `variantId: 1234`, so a wishlist
//event for the shorter id flipped — and rewrote the onclick ids of — the
//heart belonging to the longer id. That only bites when one id on the page
//is a strict numeric prefix of another (i.e. across digit-length
//boundaries), but the failure is exactly the mis-wiring ECSR-601 fixed.
//Requiring a non-digit after the id makes the match exact, and covers both
//the quoted and unquoted forms buy-buttons.liquid and cart-drawer.liquid
//emit.
const HAS_VARIANT_CONTEXT = /variantId:\s*['"]*\d+/;

const onclickIdMatches = (onclickAttr, key, id) => {
  //Ids come from Liquid and are always numeric; refuse anything else rather
  //than interpolating it into a RegExp.
  if (!/^\d+$/.test(String(id ?? ''))) return false;
  return new RegExp(`${key}:\\s*['"]?${id}(?!\\d)`).test(onclickAttr);
};

//ECSR-601: only hearts for the SAME variant flip. Matching on productId too
//flipped sibling-variant hearts (and rewired their onclick to this variant's
//ids), so saving Green marked a Blue cart line as saved. Hearts without any
//variant in their onclick still fall back to the product match.
window.updateWishlistButtonState = (variantId, productId, isInWishlist = true) => {
  getAllWishlistButtons().forEach(button => {
    const onclickAttr = button.getAttribute('onclick');
    if (!onclickAttr) return;

    const hasVariantContext = HAS_VARIANT_CONTEXT.test(onclickAttr);
    const variantMatches = onclickIdMatches(onclickAttr, 'variantId', variantId);
    const productMatches = onclickIdMatches(onclickAttr, 'productId', productId);

    if (variantMatches || (!hasVariantContext && productMatches)) {
      applyWishlistButtonState(button, variantId, productId, isInWishlist);
    }
  });
};



//ECSR-604: activating a saved heart removes the item IN PLACE — no toast-then-
//redirect to /pages/wishlist. Client chose Option B on the ticket. Previously
//a shopper tapping a filled heart in the mini cart was navigated away from
//their cart mid-purchase.
//
//Applied to every heart, not just the mini cart: `removeFromWishlist` is the
//single handler behind all of them, and having the PDP redirect while the mini
//cart toggles would be inconsistent. Flagged on ECSR-604 for confirmation.
//
//The redirect is kept as a fallback for the case where Swym's delete API or
//the list id genuinely is not available, so the heart never becomes a dead
//control.
window.removeFromWishlist = ({variantId, productId, url}) => {
  const redirectToWishlist = () => {
    if (window._swat && window._swat.ui && typeof window._swat.ui.showSuccessNotification === 'function') {
      window._swat.ui.showSuccessNotification({
        message: '💙 Redirecting to your wishlist to manage this item...'
      });
    } else {
      showWishlistFallbackToast('💙 Redirecting to your wishlist to manage this item...', 'info');
    }
    announceWishlistStatus('Redirecting to your wishlist to manage this item.');

    setTimeout(() => {
      window.location.href = '/pages/wishlist';
    }, 1000);
  };

  const performRemove = (listId) => {
    window._swat.deleteFromList(listId, {epi: variantId, empi: productId, du: url}, () => {
      if (window._swat.ui && typeof window._swat.ui.showSuccessNotification === 'function') {
        window._swat.ui.showSuccessNotification({ message: '💔 Removed from wishlist.' });
      } else {
        showWishlistFallbackToast('💔 Removed from wishlist.', 'info');
      }
      announceWishlistStatus('Removed from wishlist.');

      //Keep the local cache in step so other hearts for the same variant on
      //this page resolve correctly without waiting for a refetch.
      if (Array.isArray(window.wishlistContents)) {
        window.wishlistContents = window.wishlistContents.filter(
          (item) => String(item.variantId) !== String(variantId)
        );
      }

      //Flips this heart back to unsaved and rewires its onclick to addToWishlist.
      updateWishlistButtonState(variantId, productId, false);

      //Refresh the wishlist page grid if we happen to be on it.
      if (typeof window.renderWishlistItems === 'function') {
        window.renderWishlistItems(window.wishlistContents);
      }
    }, (error) => {
      console.error('Swym wishlist remove error:', error);
      if (window._swat.ui && typeof window._swat.ui.showErrorNotification === 'function') {
        window._swat.ui.showErrorNotification({ message: 'Error removing from wishlist. Please try again.' });
      } else {
        showWishlistFallbackToast('Error removing from wishlist. Please try again.', 'error');
      }
      announceWishlistStatus('Error removing from wishlist. Please try again.', true);
    });
  };

  if (!window._swat || typeof window._swat.deleteFromList !== 'function') {
    redirectToWishlist();
    return;
  }

  if (window._swat.swymCustomWishlistSelectedListId) {
    performRemove(window._swat.swymCustomWishlistSelectedListId);
  } else {
    //Normally already resolved at page init; resolve on demand if not.
    window.ensureSingleWishlist(DEFAULT_WISHLIST_NAME, function (res) {
      if (res?.lid) {
        window._swat.swymCustomWishlistSelectedListId = res.lid;
        performRemove(res.lid);
      } else {
        console.error('Failed to resolve wishlist for removal');
        redirectToWishlist();
      }
    });
  }
};



//ECSR-601: variant-first wishlist matching. A heart tied to a specific
//variant (mini cart lines, PDP, product cards) must only read as saved when
//that exact variant is in the wishlist — otherwise a saved sibling variant
//makes the heart render filled and reroutes the click to "manage in
//wishlist", leaving no way to save this variant. The product-id fallback
//applies only when the heart carries no variant context at all.
const isVariantInWishlist = (variantId, productId, wishlistItems) => {
  //ECSR-606: Array.isArray already rejects null/undefined.
  if (!Array.isArray(wishlistItems)) return false;
  if (variantId) {
    return wishlistItems.some(item => String(item.variantId) === String(variantId));
  }
  return Boolean(productId) &&
    wishlistItems.some(item => String(item.productId) === String(productId));
};

window.updateWishlistButtonsFromContents = (wishlistItems) => {
  //Guarded here too so an unusable payload skips the DOM walk entirely.
  if (!Array.isArray(wishlistItems)) return;

  getAllWishlistButtons().forEach(button => {
    const onclickAttr = button.getAttribute('onclick');
    if (!onclickAttr) return;

    const variantMatch = onclickAttr.match(/variantId:\s*['"]*(\d+)['"]*/) || onclickAttr.match(/variantId:\s*(\d+)/);
    const productMatch = onclickAttr.match(/productId:\s*['"]*(\d+)['"]*/) || onclickAttr.match(/productId:\s*(\d+)/);

    if (variantMatch || productMatch) {
      const variantId = variantMatch ? variantMatch[1] : null;
      const productId = productMatch ? productMatch[1] : null;
      const isInWishlist = isVariantInWishlist(variantId, productId, wishlistItems);

      applyWishlistButtonState(button, variantId, productId, isInWishlist);
    }
  });
};

window.fetchWishlistContents = (listId) => {
  const onWishlistLoaded = (details) => {
    window.wishlistContents = details.items.map(item => ({
      name: item?.dt,
      price: item?.pr,
      image: item?.iu,
      url: item?.du,
      variantId: item?.epi,
      productId: item?.empi
    }));
    
    updateWishlistButtonsFromContents(window.wishlistContents);
    renderWishlistItems(window.wishlistContents);
  };

  //ECSR-603: the error path was an empty function. A failed fetch leaves
  //every heart on the site showing the unsaved state with no signal anywhere
  //that the wishlist never loaded.
  const onWishlistLoadFailed = (error) => {
    console.error('[wishlist] Could not load wishlist contents; hearts will show unsaved state.', error);
  };

  window._swat.fetchListDetails({
    lid: listId
  }, onWishlistLoaded, onWishlistLoadFailed);
};

window.renderWishlistItems = (items) => {
  const container = document.getElementById('account-wishlist-render-container');
  if (!container) return;
  
  const displayItems = items.slice(0, 5);
  
  if (displayItems.length === 0) {
    container.innerHTML = '<div class="tw-text-center tw-py-8"><p class="tw-text-gray-600">No items in wishlist</p></div>';
    return;
  }
  
  const itemsHtml = displayItems.map(item => {
    const productName = item.name || 'Product';
    const productPrice = item.price ? window.Shopify.formatMoney(item.price * 100) : 'Price not available';
    const productImage = item.image || '';
    const productUrl = item.url || '#';
    const nameParts = productName.split(/\n|\r\n|\r/);
    const title = nameParts[0] || productName;
    const subtitle = nameParts[1] || '';
    
    return `
      <div class="tw-text-center">
        <a href="${productUrl}" class="tw-block">
          <div class="tw-bg-gray-200 tw-aspect-square tw-mb-4 tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
            ${productImage ? 
    `<img src="${productImage}" alt="${title}" class="tw-w-full tw-h-full tw-object-cover">` : 
    '<span class="tw-text-gray-500">Product Image</span>'
}
          </div>
          <h3 class="tw-font-medium tw-text-gray-900 tw-mb-1">${title}</h3>
          ${subtitle ? `<p class="tw-text-gray-900 tw-mb-1">${subtitle}</p>` : ''}
          <p class="tw-text-gray-900">${productPrice}</p>
        </a>
      </div>
    `;
  }).join('');
  
  container.innerHTML = `<div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">${itemsHtml}</div>`;
};

window.updateProductFormWishlistButton = () => {
  const variantInput = document.querySelector('.product-variant-id');
  //ECSR-555 review fix: the PDP now renders TWO wishlist buttons (title-block
  //heart on mobile + buy-buttons CTA on desktop). Update every match, not just
  //the first one in the DOM, or the visible button keeps the stale variant id.
  const wishlistButtons = document.querySelectorAll('[id*="AddToWishlistButton"]');

  if (!variantInput || !wishlistButtons.length) return;

  const currentVariantId = variantInput.value;
  if (!currentVariantId) return;

  //Release integration note: this loop combines the two lines of work that
  //touched it. ECSR-555 made it iterate EVERY heart (the mobile PDP now has a
  //title-block heart as well as the desktop buy-buttons CTA); ECSR-601/602
  //supplied the variant-accurate match and the per-product labels that run
  //inside it.
  wishlistButtons.forEach((wishlistButton) => {
    const onclickAttr = wishlistButton.getAttribute('onclick');
    const productMatch = onclickAttr ? onclickAttr.match(/productId:\s*['"]*(\d+)['"]*/) : null;
    const productId = productMatch ? productMatch[1] : null;

    if (!productId) return;

    const newOnclick = `addToWishlist({ variantId: ${currentVariantId}, productId: ${productId}, url: window.location.href })`;
    wishlistButton.setAttribute('onclick', newOnclick);

    if (Array.isArray(window.wishlistContents)) {
      //ECSR-601: the PDP heart is tied to the currently selected variant, so it
      //matches on that variant only — a saved sibling variant must not fill it.
      const isInWishlist = isVariantInWishlist(currentVariantId, null, window.wishlistContents);

      if (isInWishlist) {
        wishlistButton.classList.add('swym-added');
        wishlistButton.setAttribute('title', 'Remove from wishlist');
        wishlistButton.setAttribute('aria-label', wishlistButtonLabel(wishlistButton, true));
        wishlistButton.setAttribute('onclick', `removeFromWishlist({ variantId: ${currentVariantId}, productId: ${productId}, url: window.location.href })`);
      } else {
        wishlistButton.classList.remove('swym-added');
        wishlistButton.setAttribute('title', 'Add to wishlist');
        wishlistButton.setAttribute('aria-label', wishlistButtonLabel(wishlistButton, false));
      }
    }
  });
};

window.ensureSingleWishlist = (defaultName = 'My Wishlist', callbackFn, errorFn) => {
  window._swat.fetchLists({
    callbackFn: (lists = []) => {
      const ordered = lists.slice().reverse();
      const selected = ordered[0];

      if (!selected) {
        window._swat.createList(
          { lname: defaultName },
          (resp) => {
            const lid = resp?.lid;
            if (lid) window._swat.swymCustomWishlistSelectedListId = lid;
            if (callbackFn) callbackFn({ lid, created: true, renamed: false });
          },
          errorFn || ((err) => console.error('Error creating single wishlist', err))
        );
        return;
      }

      const lid = selected.lid;
      window._swat.swymCustomWishlistSelectedListId = lid;

      if (defaultName && selected.lname && selected.lname !== defaultName) {
        window._swat.updateListDetails(
          { lid, lname: defaultName },
          () => {
            if (callbackFn) callbackFn({ lid, created: false, renamed: true });
          },
          errorFn || ((err) => console.error('Error renaming wishlist', err))
        );
      } else {
        if (callbackFn) callbackFn({ lid, created: false, renamed: false });
      }
    },
    errorFn: errorFn || ((err) => console.error('Error fetching lists', err))
  });
};

window.getSingleWishlistLid = (callbackFn, errorFn, defaultName = 'My Wishlist') => {
  window.ensureSingleWishlist(defaultName, (res) => {
    if (callbackFn) callbackFn(res?.lid);
  }, errorFn);
};

(function () {
  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function () {
    window.ensureSingleWishlist(DEFAULT_WISHLIST_NAME, function (res) {
      if (res?.lid) {
        window._swat.swymCustomWishlistSelectedListId = res.lid;
        window.fetchWishlistContents(res.lid);
      }
    });
  });

  window.SwymCallbacks.push(function () {
    window._swat.evtLayer.addEventListener('sw:addedtocart', function () {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wishlist' });
    });

    window._swat.evtLayer.addEventListener('sw:addedtolist', function (event) {
      //`du` is Swym's deep link for the item; cache it so an entry written by
      //this path (when the event wins the race with the addToWishlist success
      //callback) still renders a real product link on the account grid.
      const { epi: variantId, empi: productId, du: url } = event.detail || {};
      if (variantId && productId) {
        addToWishlistContentsCache({ variantId, productId, url });
        updateWishlistButtonState(variantId, productId, true);
      }
    });

    window._swat.evtLayer.addEventListener('sw:removedfromlist', function (event) {
      const { epi: variantId, empi: productId } = event.detail || {};
      if (variantId && productId) {
        if (Array.isArray(window.wishlistContents)) {
          //ECSR-561 review fix: match on variantId ONLY. The previous
          //`variantId !== x && productId !== y` predicate also dropped SIBLING
          //variants of the same product (De Morgan: it keeps an entry only if it
          //differs on both fields), so removing one fabric/finish variant silently
          //purged the others from the local cache and their hearts read as
          //un-wishlisted until a full page reload.
          window.wishlistContents = window.wishlistContents.filter(
            (item) => String(item.variantId) !== String(variantId)
          );
        }
        updateWishlistButtonState(variantId, productId, false);
      }
    });
  });

  //ECSR-561 review fix: re-apply wishlist state after the cart drawer / cart
  //page actually re-renders its line items. A PUB_SUB_EVENTS.cartUpdate
  //subscriber is too early: publish() runs subscribers before CartItems'
  //Section Rendering fetch resolves and replaces the drawer DOM (and the
  //quick-add flow defers its render until the modal closes), so a pubsub
  //repaint lands on markup that is thrown away moments later. Observing the
  //cart containers for child-list changes re-syncs after the real DOM lands,
  //whatever the render path. Our own repaint only mutates attributes/classes,
  //which never fire childList mutations, so this cannot loop.
  document.addEventListener('DOMContentLoaded', function () {
    let resyncQueued = false;
    const scheduleWishlistResync = function () {
      if (resyncQueued) return;
      resyncQueued = true;
      setTimeout(function () {
        resyncQueued = false;
        if (Array.isArray(window.wishlistContents)) {
          window.updateWishlistButtonsFromContents(window.wishlistContents);
        }
      }, 0);
    };
    //ECSR-1146: product-recommendations carousels fetch their product cards
    //after page load, so their hearts land after the wishlist's page-wide
    //repaint; the same resync paints them.
    document.querySelectorAll('cart-drawer, cart-items, product-recommendations').forEach(function (container) {
      new window.MutationObserver(scheduleWishlistResync).observe(container, {
        childList: true,
        subtree: true
      });
    });
  });

  document.addEventListener('DOMContentLoaded', function() {
    const variantInput = document.querySelector('.product-variant-id');
    if (variantInput) {
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            updateProductFormWishlistButton();
          }
        });
      });
      
      observer.observe(variantInput, { 
        attributes: true, 
        attributeFilter: ['value'] 
      });
      
      variantInput.addEventListener('change', updateProductFormWishlistButton);
    }

    document.addEventListener('react-variant-change', function(event) {
      updateProductFormWishlistButton();
    });
  });
})();